import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
// Use sessionStorage rather than localStorage: tokens are wiped when the tab
// closes, shrinking the XSS exfiltration window. Long-lived auth should move
// to httpOnly cookies + CSRF before production scale.
const TOKEN_KEY = "storyforge_token";
const tokenStore = typeof window !== "undefined" ? window.sessionStorage : null;

// One-time migration of any existing localStorage token so users mid-session
// aren't kicked out the moment this build deploys.
if (typeof window !== "undefined") {
  try {
    const legacy = window.localStorage?.getItem(TOKEN_KEY);
    if (legacy && tokenStore && !tokenStore.getItem(TOKEN_KEY)) {
      tokenStore.setItem(TOKEN_KEY, legacy);
    }
    window.localStorage?.removeItem(TOKEN_KEY);
  } catch (e) {
    // Storage disabled (e.g. private mode) — auth still works for the session
    // via in-memory axios header; user simply re-logs next visit.
    console.warn("Token storage unavailable — legacy token migration skipped:", e);
  }
}

export const getToken = () => tokenStore?.getItem(TOKEN_KEY) || "";
export const setToken = (t) => {
  if (!tokenStore) return;
  if (t) tokenStore.setItem(TOKEN_KEY, t);
  else tokenStore.removeItem(TOKEN_KEY);
};

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// --- Auth ---
export const register = (payload) =>
  api.post("/auth/register", payload).then((r) => r.data);
export const login = (payload) =>
  api.post("/auth/login", payload).then((r) => r.data);
export const me = () => api.get("/auth/me").then((r) => r.data);
export const logoutApi = () => api.post("/auth/logout").then((r) => r.data);
export const googleSession = (session_id) =>
  api.post("/auth/google/session", { session_id }).then((r) => r.data);
export const updateProfile = (payload) =>
  api.put("/me", payload).then((r) => r.data);

// --- Email verification & password reset ---
export const verifyEmail = (token) =>
  api.post("/auth/verify-email", { token }).then((r) => r.data);
export const resendVerification = () =>
  api.post("/auth/resend-verification").then((r) => r.data);
export const forgotPassword = (email) =>
  api.post("/auth/forgot-password", { email }).then((r) => r.data);
export const resetPassword = (token, password) =>
  api.post("/auth/reset-password", { token, password }).then((r) => r.data);
export const exportMyData = () =>
  api.get("/auth/export-data", { responseType: "blob" }).then((r) => r.data);
export const deleteAccount = (confirm) =>
  api.post("/auth/delete-account", { confirm }).then((r) => r.data);

// --- Plans / subscription ---
export const listPlans = () => api.get("/plans").then((r) => r.data);
export const subscribe = (plan) =>
  api.post("/me/subscribe", { plan }).then((r) => r.data);
export const cancelPlan = () => api.post("/me/cancel").then((r) => r.data);

// --- Contact ---
export const sendContact = (payload) =>
  api.post("/contact", payload).then((r) => r.data);

// --- Payments (Razorpay) ---
export const getPaymentsConfig = () =>
  api.get("/payments/config").then((r) => r.data);
export const createRazorpayOrder = (plan) =>
  api.post("/payments/create-order", { plan }).then((r) => r.data);
export const verifyRazorpayPayment = (payload) =>
  api.post("/payments/verify-payment", payload).then((r) => r.data);
export const cancelRazorpayOrder = (plan) =>
  api.post("/payments/cancel-order", { plan }).then((r) => r.data);
export const getPaymentHistory = () =>
  api.get("/payments/history").then((r) => r.data);
export const paymentReceiptUrl = (orderId) => {
  const search = new URLSearchParams({ token: getToken() });
  return `${API}/payments/${orderId}/receipt?${search.toString()}`;
};

// --- Admin ---
export const adminListInquiries = (params = {}) =>
  api.get("/admin/inquiries", { params }).then((r) => r.data);
export const adminUpdateInquiryStatus = (id, status) =>
  api.patch(`/admin/inquiries/${id}`, { status }).then((r) => r.data);
export const adminDeleteInquiry = (id) =>
  api.delete(`/admin/inquiries/${id}`).then((r) => r.data);
export const adminInquiriesCsvUrl = (params = {}) => {
  const search = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );
  search.set("token", getToken());
  return `${API}/admin/inquiries/export.csv?${search.toString()}`;
};

// --- Models / Stories / Projects ---
export const listModels = () => api.get("/models").then((r) => r.data);
export const listStories = (projectId) =>
  api
    .get("/stories", { params: projectId !== undefined ? { project_id: projectId } : {} })
    .then((r) => r.data);
export const getStory = (id) => api.get(`/stories/${id}`).then((r) => r.data);
export const generateStory = (payload) =>
  api.post("/stories/generate", payload, { timeout: 120000 }).then((r) => r.data);
export const saveGeneratedStory = (payload) =>
  api.post("/stories/save", payload).then((r) => r.data);
export const updateStory = (id, story, project_id) =>
  api
    .put(`/stories/${id}`, project_id !== undefined ? { story, project_id } : { story })
    .then((r) => r.data);
export const assignStoryProject = (id, project_id) =>
  api.patch(`/stories/${id}/project`, { project_id }).then((r) => r.data);
export const deleteStory = (id) =>
  api.delete(`/stories/${id}`).then((r) => r.data);
export const exportStoryUrl = (id, format, language = "en") =>
  `${API}/stories/${id}/export?format=${format}&language=${language}&token=${encodeURIComponent(getToken())}`;

export const translateStoryPayload = (payload, language) =>
  api
    .post("/stories/translate", { payload, language }, { timeout: 90000 })
    .then((r) => r.data);

export const listProjects = () => api.get("/projects").then((r) => r.data);
export const createProject = (payload) =>
  api.post("/projects", payload).then((r) => r.data);
export const updateProject = (id, payload) =>
  api.put(`/projects/${id}`, payload).then((r) => r.data);
export const deleteProject = (id) =>
  api.delete(`/projects/${id}`).then((r) => r.data);

// --- Jira ---
export const getJiraSettings = () => api.get("/settings/jira").then((r) => r.data);
export const saveJiraSettings = (payload) =>
  api.put("/settings/jira", payload).then((r) => r.data);
export const testJiraSettings = (payload) =>
  api.post("/settings/jira/test", payload || null, { timeout: 30000 }).then((r) => r.data);
export const disconnectJira = () =>
  api.delete("/settings/jira").then((r) => r.data);
export const pushStoryToJira = (id, payload) =>
  api.post(`/stories/${id}/push/jira`, payload || {}, { timeout: 30000 }).then((r) => r.data);

// --- SSE streaming ---
export async function streamGenerateStory(payload, { onMeta, onToken, onDone, onError, onFallback, signal }) {
  const res = await fetch(`${API}/stories/generate/stream`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(payload),
    signal,
  });
  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => "");
    onError?.({ message: text || `HTTP ${res.status}` });
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let receivedTerminal = false;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const raw = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      let event = "message";
      let data = "";
      for (const line of raw.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data += line.slice(5).trim();
      }
      if (!data) continue;
      let parsed;
      try {
        parsed = JSON.parse(data);
      } catch {
        continue;
      }
      if (event === "meta") onMeta?.(parsed);
      else if (event === "token") onToken?.(parsed);
      else if (event === "done") {
        receivedTerminal = true;
        onDone?.(parsed);
      } else if (event === "error") {
        receivedTerminal = true;
        onError?.(parsed);
      } else if (event === "fallback") onFallback?.(parsed);
    }
  }
  // Stream ended. If we never got a done/error event the connection was cut
  // (e.g. ingress/proxy timeout on a long generation) — surface an actionable error
  // so the UI doesn't silently return the user to the input screen.
  if (!receivedTerminal) {
    onError?.({
      message:
        "Generation was cut off before completing (production ingress caps HTTP streams at ~60s). This is a known issue with Claude Sonnet 4.5's extended-reasoning phase. Try again with GPT-5.2 (default) — it completes in ~55s and works reliably.",
    });
  }
}

// --- Plan capabilities helper (mirror of backend PLANS) ---
export const PLAN_CAPS = {
  free:    { label: "Free",       price: 0,     stories: 1,        exports: ["markdown","json"], jira: false, projects: false, reroll: false, screenshots: false },
  trial:   { label: "Free Trial", price: 0,     stories: Infinity, exports: ["markdown","json","pdf","docx","pptx"], jira: true, projects: true, reroll: true, screenshots: true },
  weekly:  { label: "Weekly",     price: 1500,  stories: 5,        exports: ["markdown","json"], jira: false, projects: false, reroll: false, screenshots: false },
  monthly: { label: "Monthly",    price: 5000,  stories: 50,       exports: ["markdown","json","pdf","docx","pptx"], jira: true, projects: false, reroll: false, screenshots: true },
  yearly:  { label: "Yearly",     price: 30000, stories: Infinity, exports: ["markdown","json","pdf","docx","pptx"], jira: true, projects: true,  reroll: true,  screenshots: true },
};
export const planCaps = (user) => PLAN_CAPS[user?.plan || "free"] || PLAN_CAPS.free;
